import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import type { MouseEvent, Ref } from 'react';

/**
 * A bare icon button used across the editor's compact controls (line
 * toolbar, step rail, media tiles, list rows). Callers pass sizing and
 * color via className; behavior and a11y wiring stay in one place.
 */
export function IconActionButton({
   ref,
   label,
   icon,
   onClick,
   onMouseDown,
   disabled = false,
   pressed,
   className,
   iconClassName,
   testId,
}: {
   /** For callers that move focus back here after a control unmounts. */
   ref?: Ref<HTMLButtonElement>;
   label: string;
   icon: string;
   onClick: () => void;
   /**
    * Buttons that act on a focused field pass a preventDefault here: without
    * it mousedown blurs the field first, and the click lands on a button that
    * has already re-rendered for the post-blur state.
    */
   onMouseDown?: (event: MouseEvent<HTMLButtonElement>) => void;
   disabled?: boolean;
   /** Toggle buttons pass their current state; omit for one-shot actions. */
   pressed?: boolean;
   className?: string;
   iconClassName?: string;
   testId?: string;
}) {
   return (
      <button
         ref={ref}
         type="button"
         aria-label={label}
         aria-pressed={pressed}
         title={label}
         disabled={disabled}
         onClick={onClick}
         onMouseDown={onMouseDown}
         data-testid={testId}
         className={cn(
            // Callers size these compactly (size-7 is common); on touch,
            // grow the hit area past the visible icon instead of the icon
            // itself, so dense rows keep their density.
            'relative flex items-center justify-center rounded outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 pointer-coarse:after:absolute pointer-coarse:after:-inset-2 pointer-coarse:after:content-[""]',
            className
         )}
      >
         <FaIcon name={icon} className={cn('size-3.5', iconClassName)} />
      </button>
   );
}

/** The line editor's compact toolbar flavor of IconActionButton. */
export function LineButton(props: {
   label: string;
   icon: string;
   disabled: boolean;
   /** Pressed-state highlight, e.g. Bold while the cursor sits in bold text. */
   isActive?: boolean;
   /** Hovers red like the other trash cans instead of gray. */
   isDestructive?: boolean;
   onClick: () => void;
}) {
   const { isActive, isDestructive, ...buttonProps } = props;
   return (
      <IconActionButton
         {...buttonProps}
         pressed={isActive}
         className={cn(
            // These carry no button-base, so they miss its
            // disabled:pointer-events-none and :hover keeps matching while
            // disabled. The routinely-disabled delete would otherwise hover
            // red, so both fill and text need an explicit disabled: reset.
            'size-7 text-gray-500 hover:bg-gray-200 disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-gray-500',
            isDestructive ? 'hover:text-red-600' : 'hover:text-gray-800',
            isActive && 'bg-gray-200 text-gray-900'
         )}
      />
   );
}
